import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useIncomingWebhooks } from '../context/IncomingWebhooksContext';
import { SUPPORTED_COUNTRY_ALPHA2, SUPPORTED_COUNTRY_ALPHA2_SET } from '../constants/countries';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import './webhooks.css';

countries.registerLocale(enCountries);

export default function IncomingWebhooks() {
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const {
    events,
    refreshEvents,
    loading,
    error,
    incomingSettings,
    refreshIncomingSettings,
    createIncomingSetting,
    updateIncomingSetting,
    deleteIncomingSetting,
    incomingSettingsLoading,
    incomingSettingsError,
    incomingSettingsSaving,
    clearIncomingSettingsError,
  } = useIncomingWebhooks();

  const [historyEvent, setHistoryEvent] = useState(null);
  const [incomingModalMode, setIncomingModalMode] = useState(null);
  const [incomingForm, setIncomingForm] = useState({
    id: null,
    provider: '',
    url: '',
    country_alpha2: '',
  });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    const fromMs = from ? new Date(from).getTime() : null;
    const toMs = to ? new Date(to).getTime() : null;

    return list.filter((e) => {
      const ts = e?.timestamp ? new Date(e.timestamp).getTime() : null;
      if (ts == null || Number.isNaN(ts)) return true;
      if (fromMs != null && !Number.isNaN(fromMs) && ts < fromMs) return false;
      if (toMs != null && !Number.isNaN(toMs) && ts > toMs) return false;
      return true;
    });
  }, [events, from, to]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const formatTs = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  const payloadPreview = (payload) => {
    if (payload == null) return '—';
    if (typeof payload === 'string') return payload.length > 160 ? payload.slice(0, 160) + '…' : payload;
    try {
      const s = JSON.stringify(payload);
      return s.length > 160 ? s.slice(0, 160) + '…' : s;
    } catch {
      return String(payload);
    }
  };

  const toAlpha2 = (numeric) => {
    if (numeric == null) return null;
    const n = String(numeric).padStart(3, '0');
    try {
      return countries.numericToAlpha2(n);
    } catch {
      return null;
    }
  };

  const getCountryName = (alpha2) => {
    if (!alpha2) return null;
    try {
      const lng = typeof window !== 'undefined' && window.navigator?.language
        ? String(window.navigator.language).slice(0, 2)
        : 'en';
      return countries.getName(alpha2, lng) || countries.getName(alpha2, 'en') || null;
    } catch {
      return null;
    }
  };

  const countryLabel = (alpha2) => {
    if (!alpha2) return '—';
    const name = getCountryName(alpha2);
    return name ? `${name} (${alpha2})` : alpha2;
  };

  const alpha2ToCountryCodeNumeric = (alpha2) => {
    if (!alpha2) return null;
    try {
      const num = countries.alpha2ToNumeric(String(alpha2).toUpperCase());
      if (num == null || num === '') return null;
      const n = parseInt(String(num), 10);
      return Number.isNaN(n) ? null : n;
    } catch {
      return null;
    }
  };

  const openIncomingCreate = () => {
    clearIncomingSettingsError?.();
    setIncomingForm({
      id: null,
      provider: '',
      url: '',
      country_alpha2: SUPPORTED_COUNTRY_ALPHA2[0] || '',
    });
    setIncomingModalMode('create');
  };

  const openIncomingEdit = (row) => {
    clearIncomingSettingsError?.();
    const rawAlpha2 = toAlpha2(row?.country_code);
    const alpha2 =
      rawAlpha2 && SUPPORTED_COUNTRY_ALPHA2_SET.has(rawAlpha2) ? rawAlpha2 : SUPPORTED_COUNTRY_ALPHA2[0] || '';
    setIncomingForm({
      id: row?.id ?? null,
      provider: row?.provider != null ? String(row.provider) : '',
      url: row?.url != null ? String(row.url) : '',
      country_alpha2: alpha2,
    });
    setIncomingModalMode('edit');
  };

  const closeIncomingModal = () => {
    setIncomingModalMode(null);
  };

  const submitIncomingModal = async () => {
    const country_code = alpha2ToCountryCodeNumeric(incomingForm.country_alpha2);
    if (country_code == null) {
      return;
    }
    const provider = incomingForm.provider.trim();
    const url = incomingForm.url.trim();
    if (!provider || !url) {
      return;
    }
    if (incomingModalMode === 'create') {
      const r = await createIncomingSetting({ provider, url, country_code });
      if (r?.ok) closeIncomingModal();
      return;
    }
    if (incomingModalMode === 'edit' && incomingForm.id != null) {
      const r = await updateIncomingSetting({
        id: incomingForm.id,
        provider,
        url,
        country_code,
      });
      if (r?.ok) closeIncomingModal();
    }
  };

  const onDeleteIncomingSetting = async () => {
    if (incomingModalMode !== 'edit' || incomingForm.id == null) return;
    const ok = window.confirm(t('confirmRemoveIncomingWebhookSetting'));
    if (!ok) return;
    const r = await deleteIncomingSetting(incomingForm.id);
    if (r?.ok) closeIncomingModal();
  };

  const onRefresh = async () => {
    setPage(1);
    const fromIso = from ? new Date(from).toISOString() : undefined;
    const toIso = to ? new Date(to).toISOString() : undefined;
    await refreshEvents?.({ from: fromIso, to: toIso });
  };

  const openHistory = (evt) => {
    setHistoryEvent(evt || null);
  };

  const closeHistory = () => {
    setHistoryEvent(null);
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          padding: 24,
          boxSizing: 'border-box'
        }}
      >
        <h1 style={{ marginTop: 0, marginBottom: 16, color: currentTheme.text }}>
          {t('incomingWebhooks')}
        </h1>

        <div
          style={{
            borderRadius: 12,
            background: currentTheme.surface,
            border: `1px solid ${currentTheme.border}`,
            boxShadow: currentTheme.cardShadow,
            overflow: 'hidden',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              borderBottom: `1px solid ${currentTheme.border}`,
              background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
            }}
          >
            <div style={{ fontWeight: 800, color: currentTheme.text }}>
              {t('incomingWebhooksSettingsPanel')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                onClick={openIncomingCreate}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: 'none',
                  background: theme === 'light' ? '#4F46E5' : '#6366F1',
                  color: '#fff',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('create')}
              </button>
              <button
                type="button"
                onClick={() => refreshIncomingSettings()}
                disabled={incomingSettingsLoading}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                  color: currentTheme.text,
                  cursor: incomingSettingsLoading ? 'not-allowed' : 'pointer',
                  opacity: incomingSettingsLoading ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('refresh')}
              </button>
            </div>
          </div>

          {incomingSettingsLoading && (
            <div style={{ padding: 12, color: currentTheme.textSecondary }}>
              {t('loading')}
            </div>
          )}

          {!incomingSettingsLoading && incomingSettingsError && (
            <div
              style={{
                margin: 12,
                padding: 12,
                borderRadius: 12,
                background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                color: currentTheme.text,
              }}
            >
              {incomingSettingsError}
            </div>
          )}

          {!incomingSettingsLoading && !incomingSettingsError && (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: 12,
                      fontSize: 12,
                      color: currentTheme.textSecondary,
                      borderBottom: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    {t('Provider')}
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: 12,
                      fontSize: 12,
                      color: currentTheme.textSecondary,
                      borderBottom: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    {t('URL')}
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: 12,
                      fontSize: 12,
                      color: currentTheme.textSecondary,
                      borderBottom: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    {t('country')}
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: 12,
                      fontSize: 12,
                      color: currentTheme.textSecondary,
                      borderBottom: `1px solid ${currentTheme.border}`,
                      width: 84,
                    }}
                  >
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(incomingSettings) ? incomingSettings : []).map((row, idx, arr) => {
                  const alpha2 = toAlpha2(row?.country_code);
                  const code = alpha2 ? String(alpha2).toLowerCase() : '';

                  return (
                    <tr key={row?.id ?? idx}>
                      <td
                        style={{
                          padding: 12,
                          borderBottom: idx === arr.length - 1 ? 'none' : `1px solid ${currentTheme.border}`,
                          verticalAlign: 'top',
                          color: currentTheme.text,
                        }}
                      >
                        {row?.provider || '—'}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          borderBottom: idx === arr.length - 1 ? 'none' : `1px solid ${currentTheme.border}`,
                          verticalAlign: 'top',
                          color: currentTheme.text,
                          wordBreak: 'break-all',
                          maxWidth: 280,
                        }}
                      >
                        {row?.url || '—'}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          borderBottom: idx === arr.length - 1 ? 'none' : `1px solid ${currentTheme.border}`,
                          verticalAlign: 'top',
                          color: currentTheme.text,
                        }}
                      >
                        {alpha2 ? (
                          <span className={`fi fi-${code}`} aria-hidden="true" title={countryLabel(alpha2)} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          borderBottom: idx === arr.length - 1 ? 'none' : `1px solid ${currentTheme.border}`,
                          verticalAlign: 'top',
                          textAlign: 'right',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openIncomingEdit(row)}
                          aria-label={t('incomingWebhookSettingEditTitle')}
                          title={t('incomingWebhookSettingEditTitle')}
                          style={{
                            height: 30,
                            width: 30,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 10,
                            border: `1px solid ${currentTheme.border}`,
                            background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                            color: currentTheme.textSecondary,
                            cursor: 'pointer',
                          }}
                        >
                          <span className="oi oi-ellipses" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {(!Array.isArray(incomingSettings) || incomingSettings.length === 0) && (
                  <tr>
                    <td colSpan={6} style={{ padding: 12, color: currentTheme.textSecondary }}>
                      {t('No data')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            padding: 12,
            borderRadius: 12,
            background: currentTheme.surface,
            border: `1px solid ${currentTheme.border}`,
            boxShadow: currentTheme.cardShadow,
            marginBottom: 12
          }}
        >
          <label style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('From')}</div>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                border: `1px solid ${currentTheme.border}`,
                background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                color: currentTheme.text,
                outline: 'none'
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('To')}</div>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                border: `1px solid ${currentTheme.border}`,
                background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                color: currentTheme.text,
                outline: 'none'
              }}
            />
          </label>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: '10px 12px',
              borderRadius: 12,
              border: 'none',
              background: theme === 'light' ? '#4F46E5' : '#6366F1',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              whiteSpace: 'nowrap',
              marginLeft: 'auto'
            }}
          >
            {t('refresh')}
          </button>
        </div>

        {loading && (
          <div style={{ color: currentTheme.textSecondary }}>
            {t('Loading')}...
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
              border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
              color: currentTheme.text,
              marginBottom: 12
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div
            style={{
              borderRadius: 12,
              background: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: currentTheme.cardShadow,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                borderBottom: `1px solid ${currentTheme.border}`,
                background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
              }}
            >
              <div style={{ fontWeight: 800, color: currentTheme.text }}>
                {t('Events')}: {total}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('Items per page')}</div>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className={['webhooks-select', theme].join(' ')}
                  style={{
                    width: 'auto',
                    minWidth: 72,
                    colorScheme: theme === 'dark' ? 'dark' : 'light',
                  }}
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                      opacity: safePage <= 1 ? 0.6 : 1
                    }}
                  >
                    {t('Prev')}
                  </button>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary, whiteSpace: 'nowrap' }}>
                    {t('Page')} {safePage} {t('of')} {totalPages}
                  </div>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                      opacity: safePage >= totalPages ? 0.6 : 1
                    }}
                  >
                    {t('Next')}
                  </button>
                </div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                    {t('Timestamp')}
                  </th>
                  <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                    {t('Provider')}
                  </th>
                  <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                    {t('country')}
                  </th>
                  <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                    {t('Payload')}
                  </th>
                  <th style={{ textAlign: 'right', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 84 }}>
                    {t('History')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((e, idx) => (
                  <tr key={e?.id || `${safePage}-${idx}`}>
                    <td style={{ padding: 12, borderBottom: idx === pageItems.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', whiteSpace: 'nowrap', color: currentTheme.text }}>
                      {formatTs(e?.timestamp)}
                    </td>
                    <td style={{ padding: 12, borderBottom: idx === pageItems.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.text }}>
                      {e?.provider || '—'}
                    </td>
                    <td style={{ padding: 12, borderBottom: idx === pageItems.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.text }}>
                      {(() => {
                        const alpha2 = toAlpha2(e?.country);
                        if (!alpha2) return '—';
                        const code = String(alpha2).toLowerCase();
                        return (
                          <span
                            className={`fi fi-${code}`}
                            aria-hidden="true"
                            title={`${alpha2} (${e?.country})`}
                          />
                        );
                      })()}
                    </td>
                    <td style={{ padding: 12, borderBottom: idx === pageItems.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.textSecondary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12 }}>
                      {payloadPreview(e?.payload)}
                    </td>
                    <td style={{ padding: 12, borderBottom: idx === pageItems.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => openHistory(e)}
                        aria-label={t('Webhook history')}
                        title={t('Webhook history')}
                        style={{
                          height: 30,
                          width: 30,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 10,
                          border: `1px solid ${currentTheme.border}`,
                          background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                          color: currentTheme.textSecondary,
                          cursor: 'pointer'
                        }}
                      >
                        <span className="oi oi-ellipses" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 12, color: currentTheme.textSecondary }}>
                      {t('No data')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!!incomingModalMode && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeIncomingModal();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 520,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>
                  {incomingModalMode === 'create'
                    ? t('incomingWebhookSettingCreateTitle')
                    : t('incomingWebhookSettingEditTitle')}
                </div>
                <button
                  type="button"
                  onClick={closeIncomingModal}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer',
                  }}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('Provider')}</div>
                  <input
                    type="text"
                    value={incomingForm.provider}
                    onChange={(e) => setIncomingForm((f) => ({ ...f, provider: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('URL')}</div>
                  <input
                    type="text"
                    value={incomingForm.url}
                    onChange={(e) => setIncomingForm((f) => ({ ...f, url: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('country')}</div>
                  <select
                    value={incomingForm.country_alpha2}
                    onChange={(e) => setIncomingForm((f) => ({ ...f, country_alpha2: e.target.value }))}
                    className={['webhooks-select', theme].join(' ')}
                    style={{
                      colorScheme: theme === 'dark' ? 'dark' : 'light',
                    }}
                  >
                    {SUPPORTED_COUNTRY_ALPHA2.map((alpha2) => (
                      <option key={alpha2} value={alpha2}>
                        {countryLabel(alpha2)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {incomingSettingsError && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 10,
                    background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                    border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                    color: currentTheme.text,
                    fontSize: 13,
                  }}
                >
                  {incomingSettingsError}
                </div>
              )}

              <div
                style={{
                  marginTop: 16,
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {incomingModalMode === 'edit' && incomingForm.id != null && (
                  <button
                    type="button"
                    onClick={onDeleteIncomingSetting}
                    disabled={incomingSettingsSaving}
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      incomingSettingsSaving ? 'disabled' : 'enabled',
                    ].join(' ')}
                    style={{ marginRight: 'auto' }}
                  >
                    {t('Delete')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeIncomingModal}
                  disabled={incomingSettingsSaving}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.text,
                    cursor: incomingSettingsSaving ? 'not-allowed' : 'pointer',
                    opacity: incomingSettingsSaving ? 0.6 : 1,
                  }}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={submitIncomingModal}
                  disabled={
                    incomingSettingsSaving ||
                    !incomingForm.provider.trim() ||
                    !incomingForm.url.trim() ||
                    alpha2ToCountryCodeNumeric(incomingForm.country_alpha2) == null
                  }
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: 'none',
                    background: theme === 'light' ? '#4F46E5' : '#6366F1',
                    color: '#fff',
                    cursor:
                      incomingSettingsSaving ||
                      !incomingForm.provider.trim() ||
                      !incomingForm.url.trim() ||
                      alpha2ToCountryCodeNumeric(incomingForm.country_alpha2) == null
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      incomingSettingsSaving ||
                      !incomingForm.provider.trim() ||
                      !incomingForm.url.trim() ||
                      alpha2ToCountryCodeNumeric(incomingForm.country_alpha2) == null
                        ? 0.6
                        : 1,
                  }}
                >
                  {incomingSettingsSaving ? t('Saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {!!historyEvent && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeHistory();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 720,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>{t('Webhook history')}</div>
                <button
                  type="button"
                  onClick={closeHistory}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer'
                  }}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 12, color: currentTheme.textSecondary }}>
                {t('Incoming webhooks page is under construction')}
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 12,
                  border: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                  color: currentTheme.text,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '50vh',
                  overflow: 'auto'
                }}
              >
                {JSON.stringify(historyEvent, null, 2)}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

